import { useImperativeHandle, useRef, useState } from "react";
import type { ReactNode, Ref } from "react";
import {
  Platform,
  Text,
  TextInput as TextInputComponent,
  TouchableOpacity,
  View,
} from "react-native";
import type { BlurEvent, TextInputInstance } from "react-native";

import { useCustomTheme } from "../hooks/useCustomTheme";
import { CollapsibleView } from "./CollapsibleView";
import type { IconSet } from "./Icon";
import { Icon } from "./Icon";
import { RequiredMark, useRequiredLabel } from "./LabelText";
import { ThemedText } from "./ThemedText";
import { styles } from "./styles/TextInput.styles";

export type TextInputHandle = {
  focus: () => void;
};

export type TextInputProps = {
  ref?: Ref<TextInputHandle>;
  value: string;
  placeholder: string;
  onChangeText?: (text: string) => void;
  onBlur?: (e: BlurEvent) => void;
  type: "text" | "password" | "numeric";
  icon?: IconSet;
  error?: string | false;
  readonly?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  description?: string;
  slotRight?: ReactNode;
  isFocusedOverride?: boolean;
  isHoveredOverride?: boolean;
  reserveErrorSpace?: boolean;
  required?: boolean;
  testID?: string;
};

export const TextInput = ({
  ref,
  value,
  placeholder,
  onChangeText,
  onBlur,
  error,
  type,
  icon,
  readonly = false,
  disabled = false,
  multiline = false,
  slotRight,
  description,
  isFocusedOverride,
  isHoveredOverride,
  reserveErrorSpace = true,
  required = false,
  testID,
}: TextInputProps) => {
  const inputRef = useRef<TextInputInstance>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const { colors, fonts } = useCustomTheme();
  const requiredLabel = useRequiredLabel();
  const hasValue = value.length > 0;
  const isFocusActive = !disabled && (isFocusedOverride ?? isFocused);
  const isHoverActive = !disabled && (isHoveredOverride ?? isHovered);

  const borderColor = disabled
    ? colors.mutedBorder
    : error
      ? colors.danger
      : isFocusActive || isHoverActive
        ? colors.palette.spinach30
        : hasValue
          ? colors.palette.spinach40
          : colors.mutedBorder;
  const borderWidth = disabled
    ? 1
    : error
      ? undefined
      : isFocusActive || isHoverActive || hasValue
        ? 1
        : undefined;
  const hoverHandlers =
    Platform.OS === "web" && !disabled
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : undefined;

  const contentColor = disabled ? colors.muted : colors.text;
  const disabledContainerStyle =
    disabled && Platform.OS === "web"
      ? ({ cursor: "not-allowed" } as any)
      : undefined;
  const inputCursorStyle =
    Platform.OS !== "web"
      ? undefined
      : disabled
        ? ({ cursor: "not-allowed" } as any)
        : readonly
          ? ({ cursor: "inherit" } as any)
          : undefined;
  const focusInput = () => {
    if (readonly || disabled) return;
    inputRef.current?.focus();
  };
  useImperativeHandle(ref, () => ({ focus: focusInput }));
  const announced = required && !disabled;
  const requiredProps = required
    ? ({
        "aria-label":
          announced && Platform.OS !== "web"
            ? requiredLabel(placeholder)
            : placeholder,
        ...(announced && Platform.OS === "web"
          ? { "aria-required": true }
          : {}),
      } as any)
    : undefined;
  const containerTouchHandlers =
    !readonly && !disabled
      ? ({
          onTouchEnd: focusInput,
          ...(Platform.OS === "web" ? { onClick: focusInput } : {}),
        } as any)
      : undefined;

  return (
    <View style={styles.formGroup} {...(hoverHandlers as any)}>
      <View
        {...(containerTouchHandlers ?? {})}
        style={[
          styles.inputContainer,
          multiline && styles.inputContainerMultiline,
          { borderColor },
          ...(borderWidth !== undefined ? [{ borderWidth }] : []),
          disabledContainerStyle,
          Platform.OS === "web" &&
            (isHoverActive || isFocusActive) &&
            !disabled && [
              styles.inputContainerOutline,
              { outlineColor: borderColor },
            ],
        ]}
      >
        {icon && <Icon {...icon} size={24} color={contentColor} />}
        <View style={styles.inputGroup}>
          {placeholder && (isFocusActive || hasValue) ? (
            <Text
              style={[styles.label, { color: contentColor }]}
              aria-hidden={required || undefined}
            >
              {placeholder}
              {required ? <RequiredMark /> : null}
            </Text>
          ) : null}
          <TextInputComponent
            ref={inputRef}
            testID={testID}
            {...(Platform.OS === "web" && (readonly || disabled)
              ? ({ tabIndex: -1 } as any)
              : ({} as any))}
            {...requiredProps}
            style={[
              styles.input,
              multiline && styles.inputMultiline,
              multiline &&
                placeholder &&
                (isFocusActive || (value && value.length > 0)) &&
                styles.inputMultilineWithLabel,
              { color: contentColor },
              inputCursorStyle,
            ]}
            value={value}
            onChangeText={onChangeText}
            placeholder={
              isFocusActive
                ? undefined
                : required
                  ? `${placeholder}*`
                  : placeholder
            }
            placeholderTextColor={disabled ? colors.muted : undefined}
            secureTextEntry={!showPassword && type === "password"}
            onFocus={() => !readonly && !disabled && setIsFocused(true)}
            onBlur={(e) => {
              setIsFocused(false);
              if (onBlur) onBlur(e);
            }}
            keyboardType={type === "numeric" ? "numeric" : undefined}
            editable={!readonly && !disabled}
            aria-disabled={disabled}
            multiline={multiline}
            numberOfLines={multiline ? 4 : undefined}
          />
        </View>

        {type === "password" && (
          <TouchableOpacity
            style={styles.passwordIcon}
            onPress={togglePasswordVisibility}
            disabled={disabled}
            aria-disabled={disabled}
          >
            <Icon
              iconSet="material-community"
              icon={showPassword ? "eye-off-outline" : "eye-outline"}
              size={24}
              color={disabled ? colors.muted : "#808080"}
            />
          </TouchableOpacity>
        )}
        {slotRight}
      </View>
      {description && (
        <View
          style={[
            styles.descriptionTextContainer,
            { backgroundColor: colors.sectionBackground },
          ]}
        >
          <ThemedText type="small" style={fonts.medium}>
            {description}
          </ThemedText>
        </View>
      )}
      {reserveErrorSpace ? (
        typeof error !== "undefined" && (
          <View style={styles.errorText}>
            <ThemedText type="small" style={{ color: colors.danger }}>
              {error}
            </ThemedText>
          </View>
        )
      ) : (
        <CollapsibleView
          isVisible={Boolean(error)}
          style={styles.collapsibleErrorText}
          testID={testID ? `${testID}-error` : undefined}
        >
          <ThemedText type="small" style={{ color: colors.danger }}>
            {error}
          </ThemedText>
        </CollapsibleView>
      )}
    </View>
  );
};
